import { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SITE_URL = process.env.REACT_APP_BACKEND_URL;

function formatTime(date) {
  return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false });
}
function formatDate(date) {
  return date.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" });
}

const QUOTES = [
  { text: "Great things in business are never done by one person. They're done by a team of people.", author: "Steve Jobs" },
  { text: "The secret of getting ahead is getting started.", author: "Mark Twain" },
  { text: "Coming together is a beginning, staying together is progress, and working together is success.", author: "Henry Ford" },
  { text: "Alone we can do so little; together we can do so much.", author: "Helen Keller" },
  { text: "The strength of the team is each individual member. The strength of each member is the team.", author: "Phil Jackson" },
  { text: "Talent wins games, but teamwork and intelligence win championships.", author: "Michael Jordan" },
  { text: "If you want to go fast, go alone. If you want to go far, go together.", author: "African Proverb" },
  { text: "Innovation distinguishes between a leader and a follower.", author: "Steve Jobs" },
  { text: "The best way to predict the future is to create it.", author: "Peter Drucker" },
  { text: "Don't watch the clock; do what it does. Keep going.", author: "Sam Levenson" },
  { text: "Success usually comes to those who are too busy to be looking for it.", author: "Henry David Thoreau" },
  { text: "Your work is going to fill a large part of your life. Make it great.", author: "Steve Jobs" },
];

const WEATHER_ICONS = {
  0: "\u2600\uFE0F", 1: "\u26C5", 2: "\u26C5", 3: "\u2601\uFE0F",
  45: "\uD83C\uDF2B\uFE0F", 48: "\uD83C\uDF2B\uFE0F",
  51: "\uD83C\uDF27\uFE0F", 53: "\uD83C\uDF27\uFE0F", 55: "\uD83C\uDF27\uFE0F",
  61: "\uD83C\uDF26\uFE0F", 63: "\uD83C\uDF27\uFE0F", 65: "\uD83C\uDF27\uFE0F",
  80: "\uD83C\uDF26\uFE0F", 81: "\uD83C\uDF27\uFE0F", 82: "\u26C8\uFE0F",
  95: "\u26A1",
};

const STYLE = `
  @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;700;800&display=swap');
  * { box-sizing: border-box; margin: 0; padding: 0; }
  html, body, #root { height: 100%; overflow: hidden; background: #09090b; }

  .lobby {
    font-family: 'Space Grotesk', sans-serif;
    background: #09090b; color: #fafafa;
    height: 100vh; width: 100vw;
    display: flex; flex-direction: column;
    overflow: hidden;
    position: relative;
  }

  /* Subtle noise grain overlay */
  .lobby::before {
    content: '';
    position: fixed; inset: 0;
    background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.03'/%3E%3C/svg%3E");
    pointer-events: none; z-index: 0;
  }
  .lobby > * { position: relative; z-index: 1; }

  /* ── Top bar ─── */
  .lb-topbar {
    display: flex; justify-content: space-between; align-items: center;
    padding: 28px 36px 0;
    flex-shrink: 0;
    animation: slideDown 0.6s ease-out both;
  }
  .lb-logo {
    font-size: 20px; font-weight: 800; letter-spacing: 2px; color: #fafafa;
    text-transform: uppercase;
  }
  .lb-weather {
    display: flex; align-items: center; gap: 10px;
    font-size: 14px; color: #71717a;
    animation: fadeSlideLeft 0.8s ease-out both;
    animation-delay: 0.3s;
  }
  .lb-weather-icon { font-size: 22px; }
  .lb-weather-temp { font-size: 20px; font-weight: 700; color: #fafafa; }
  .lb-weather-desc { font-size: 12px; color: #52525b; }
  .lb-weather-extra { font-size: 11px; color: #3f3f46; margin-left: 4px; }

  /* ── Hero welcome section ─── */
  .lb-hero {
    padding: 36px 36px 16px;
    flex-shrink: 0;
  }
  .lb-welcome {
    font-size: 48px; font-weight: 800; letter-spacing: -2px; line-height: 1;
    color: #fafafa;
    animation: fadeSlideUp 0.7s ease-out both;
    animation-delay: 0.1s;
  }
  .lb-welcome span {
    background: linear-gradient(135deg, #FFD60A, #FF6B2B);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
  .lb-date {
    font-size: 14px; color: #52525b; margin-top: 8px;
    letter-spacing: 2px; text-transform: uppercase;
    animation: fadeSlideUp 0.7s ease-out both;
    animation-delay: 0.2s;
  }
  .lb-clock {
    font-size: 110px; font-weight: 800; letter-spacing: -5px;
    font-variant-numeric: tabular-nums; line-height: 1;
    color: #fafafa; margin-top: 0;
    animation: fadeSlideUp 0.8s ease-out both;
    animation-delay: 0.3s;
  }

  /* ── Availability summary ─── */
  .lb-summary {
    padding: 0 36px;
    margin-bottom: 14px;
    flex-shrink: 0;
    animation: fadeSlideUp 0.7s ease-out both;
    animation-delay: 0.4s;
  }
  .lb-summary-bar {
    display: flex; align-items: center; gap: 12px;
  }
  .lb-summary-text {
    font-size: 13px; color: #52525b; letter-spacing: 3px;
    text-transform: uppercase; font-weight: 700;
  }
  .lb-summary-count { color: #fafafa; }
  .lb-summary-dots {
    display: flex; gap: 6px;
  }
  .lb-summary-dot {
    width: 8px; height: 8px; border-radius: 50%;
    transition: background-color 0.5s ease;
  }
  .lb-summary-dot.avl { background: #2DC653; }
  .lb-summary-dot.occ { background: #3f3f46; }

  /* ── Room cards ─── */
  .lb-rooms {
    flex: 1; overflow: hidden;
    padding: 0 36px;
    display: flex; flex-direction: column;
    gap: 8px;
  }
  .lb-room {
    flex: 1;
    border: 1px solid #1c1c1f;
    border-radius: 14px;
    padding: 16px 24px;
    display: flex; align-items: center; gap: 20px;
    min-height: 0;
    transition: border-color 0.4s ease, background-color 0.4s ease, transform 0.3s ease;
    opacity: 0;
    animation: roomSlideIn 0.5s ease-out forwards;
  }
  .lb-room:hover { transform: scale(1.005); }
  .lb-room.occ { border-color: #27272a; background: rgba(255,255,255,0.02); }
  .lb-room.avl { border-color: #18181b; background: transparent; }

  /* Status dot */
  .lb-dot {
    width: 10px; height: 10px; border-radius: 50%; flex-shrink: 0;
    transition: all 0.4s ease;
  }
  .lb-dot.occ {
    background: #FFD60A;
    box-shadow: 0 0 12px rgba(255,214,10,0.4);
    animation: pulse 2.5s ease-in-out infinite;
  }
  .lb-dot.avl {
    background: #2DC653;
    box-shadow: 0 0 6px rgba(45,198,83,0.2);
  }

  /* Room info */
  .lb-room-info { flex: 1; min-width: 0; }
  .lb-room-name {
    font-size: 20px; font-weight: 800; letter-spacing: -0.5px;
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
    transition: color 0.4s ease;
  }
  .lb-room-name.avl { color: #52525b; }
  .lb-room-sub {
    font-size: 12px; color: #3f3f46; margin-top: 2px;
  }

  /* Current booking — right side */
  .lb-room-booking { text-align: right; flex-shrink: 0; max-width: 55%; }
  .lb-room-meeting {
    font-size: 22px; font-weight: 800; letter-spacing: -0.5px;
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
    line-height: 1.1;
    color: #fafafa;
  }
  .lb-room-who {
    font-size: 13px; color: #71717a; margin-top: 2px;
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  }
  .lb-room-time {
    font-size: 12px; color: #52525b; margin-top: 2px;
    font-variant-numeric: tabular-nums;
  }
  .lb-room-avail-tag {
    font-size: 11px; color: #2DC653; font-weight: 700;
    letter-spacing: 2px; text-transform: uppercase;
  }

  /* Next booking */
  .lb-room-next {
    font-size: 11px; color: #52525b; margin-top: 3px;
  }
  .lb-room-next strong { color: #71717a; }

  /* ── Divider ─── */
  .lb-divider {
    height: 1px; margin: 0 36px;
    background: linear-gradient(90deg, transparent, #27272a 20%, #27272a 80%, transparent);
    flex-shrink: 0;
  }

  /* ── Quote section ─── */
  .lb-quote-section {
    padding: 18px 36px 24px;
    flex-shrink: 0;
    min-height: 80px;
    position: relative;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 24px;
  }
  .lb-quote-inner {
    transition: opacity 0.6s ease-in-out, transform 0.6s ease-in-out;
  }
  .lb-quote-inner.entering {
    opacity: 1; transform: translateY(0);
  }
  .lb-quote-inner.exiting {
    opacity: 0; transform: translateY(8px);
  }
  .lb-quote {
    font-size: 15px; color: #52525b; font-style: italic;
    line-height: 1.6; max-width: 85%;
  }
  .lb-quote-author {
    font-size: 12px; color: #3f3f46; margin-top: 6px;
    font-style: normal; font-weight: 700; letter-spacing: 1px;
  }

  /* ── QR code section ─── */
  .lb-qr {
    flex-shrink: 0;
    display: flex; flex-direction: column; align-items: center; gap: 6px;
    animation: fadeSlideLeft 0.8s ease-out both;
    animation-delay: 0.6s;
  }
  .lb-qr-label {
    font-size: 10px; color: #3f3f46; letter-spacing: 2px;
    text-transform: uppercase; font-weight: 700; text-align: center;
  }

  /* ── Keyframes ─── */
  @keyframes fadeSlideUp {
    from { opacity: 0; transform: translateY(16px); }
    to { opacity: 1; transform: translateY(0); }
  }
  @keyframes fadeSlideLeft {
    from { opacity: 0; transform: translateX(20px); }
    to { opacity: 1; transform: translateX(0); }
  }
  @keyframes slideDown {
    from { opacity: 0; transform: translateY(-12px); }
    to { opacity: 1; transform: translateY(0); }
  }
  @keyframes roomSlideIn {
    from { opacity: 0; transform: translateX(-12px); }
    to { opacity: 1; transform: translateX(0); }
  }
  @keyframes pulse {
    0%, 100% { box-shadow: 0 0 8px rgba(255,214,10,0.3); }
    50% { box-shadow: 0 0 18px rgba(255,214,10,0.6); }
  }
`;

export default function OverviewDisplay() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [data, setData] = useState(null);
  const [weather, setWeather] = useState(null);
  const [error, setError] = useState(null);
  const [time, setTime] = useState(new Date());
  const [quoteIdx, setQuoteIdx] = useState(Math.floor(Math.random() * QUOTES.length));
  const [quotePhase, setQuotePhase] = useState("entering");
  const serverOffsetRef = useRef(0);

  const fetchData = useCallback(() => {
    if (!token) { setError("No display token provided"); return; }
    axios
      .get(`${API}/display-overview?token=${token}`)
      .then((r) => {
        setData(r.data);
        setError(null);
        if (r.data.server_time) {
          const serverNow = new Date(r.data.server_time);
          serverOffsetRef.current = serverNow.getTime() - Date.now();
          setTime(serverNow);
        }
      })
      .catch((e) => setError(e.response?.data?.detail || "Failed to load"));
  }, [token]);

  const fetchWeather = useCallback(() => {
    axios.get(`${API}/weather`).then((r) => setWeather(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    fetchData();
    fetchWeather();
    const dataInterval = setInterval(fetchData, 30000);
    const weatherInterval = setInterval(fetchWeather, 900000);
    return () => { clearInterval(dataInterval); clearInterval(weatherInterval); };
  }, [fetchData, fetchWeather]);

  useEffect(() => {
    const tick = setInterval(() => setTime(new Date(Date.now() + serverOffsetRef.current)), 1000);
    return () => clearInterval(tick);
  }, []);

  // Rotate quotes every 45s with fade transition
  useEffect(() => {
    const q = setInterval(() => {
      setQuotePhase("exiting");
      setTimeout(() => {
        setQuoteIdx((i) => (i + 1) % QUOTES.length);
        setQuotePhase("entering");
      }, 600);
    }, 45000);
    return () => clearInterval(q);
  }, []);

  useEffect(() => {
    const id = "lobby-styles";
    if (!document.getElementById(id)) {
      const style = document.createElement("style");
      style.id = id;
      style.textContent = STYLE;
      document.head.appendChild(style);
    }
  }, []);

  if (error) return (
    <div className="lobby" style={{ alignItems: "center", justifyContent: "center" }}>
      <div style={{ fontSize: "36px", fontWeight: 800 }}>Display Error</div>
      <div style={{ fontSize: "18px", color: "#71717a", marginTop: "12px" }}>{error}</div>
    </div>
  );

  if (!data) return (
    <div className="lobby" style={{ alignItems: "center", justifyContent: "center" }}>
      <div style={{ fontSize: "36px", fontWeight: 800, animation: "pulse 2s ease-in-out infinite" }}>Loading...</div>
    </div>
  );

  const rooms = data.rooms || [];
  const availCount = rooms.filter((r) => !r.is_occupied).length;
  const quote = QUOTES[quoteIdx];

  return (
    <div className="lobby" data-testid="overview-display">
      {/* Top bar: logo + weather */}
      <div className="lb-topbar">
        <div className="lb-logo" data-testid="overview-logo">The Rig</div>
        {weather && weather.temp_c !== null && (
          <div className="lb-weather" data-testid="weather-widget">
            <span className="lb-weather-icon">{WEATHER_ICONS[weather.code] || "\u2600\uFE0F"}</span>
            <span className="lb-weather-temp">{Math.round(weather.temp_c)}&deg;C</span>
            <span className="lb-weather-desc">{weather.description}</span>
            {weather.humidity && (
              <span className="lb-weather-extra">&middot; {weather.humidity}% humidity</span>
            )}
          </div>
        )}
      </div>

      {/* Hero: Welcome + Clock */}
      <div className="lb-hero">
        <div className="lb-welcome" data-testid="welcome-text">
          Welcome to <span>The Rig</span>
        </div>
        <div className="lb-date">{formatDate(time)}</div>
        <div className="lb-clock" data-testid="overview-clock">{formatTime(time)}</div>
      </div>

      {/* Availability summary */}
      <div className="lb-summary">
        <div className="lb-summary-bar">
          <div className="lb-summary-text">
            <span className="lb-summary-count">{availCount}</span> of {rooms.length} rooms available
          </div>
          <div className="lb-summary-dots">
            {rooms.map((r, i) => (
              <div key={i} className={`lb-summary-dot ${r.is_occupied ? "occ" : "avl"}`} />
            ))}
          </div>
        </div>
      </div>

      {/* Room cards */}
      <div className="lb-rooms" data-testid="overview-grid">
        {rooms.map((room, idx) => {
          const occ = room.is_occupied;
          const cur = room.current_booking;
          const nxt = room.next_booking;
          return (
            <div
              key={room.space_id}
              className={`lb-room ${occ ? "occ" : "avl"}`}
              style={{ animationDelay: `${0.5 + idx * 0.08}s` }}
              data-testid={`overview-room-${room.space_id}`}
            >
              <div className={`lb-dot ${occ ? "occ" : "avl"}`} />
              <div className="lb-room-info">
                <div className={`lb-room-name ${occ ? "" : "avl"}`}>{room.name}</div>
                {!occ && nxt && (
                  <div className="lb-room-next">Next: <strong>{nxt.start}</strong> &mdash; {nxt.meeting_name || nxt.name}</div>
                )}
                {!occ && !nxt && <div className="lb-room-sub">No bookings today</div>}
              </div>
              <div className="lb-room-booking">
                {occ && cur ? (
                  <>
                    <div className="lb-room-meeting">
                      {cur.meeting_name || cur.booked_for || cur.name || "Reserved"}
                    </div>
                    <div className="lb-room-who">
                      {cur.booked_for ? cur.booked_for : cur.name}
                      {cur.booked_for && cur.name ? ` \u00B7 by ${cur.name}` : ""}
                    </div>
                    <div className="lb-room-time">{cur.start} &mdash; {cur.end}</div>
                  </>
                ) : (
                  <div className="lb-room-avail-tag">Available</div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Divider */}
      <div className="lb-divider" />

      {/* Quote + QR */}
      <div className="lb-quote-section">
        <div className={`lb-quote-inner ${quotePhase}`} data-testid="lobby-quote">
          <div className="lb-quote">
            &ldquo;{quote.text}&rdquo;
          </div>
          <div className="lb-quote-author">&mdash; {quote.author}</div>
        </div>
        <div className="lb-qr" data-testid="lobby-qr-code">
          <QRCodeSVG
            value={`${SITE_URL}/kiosk`}
            size={72}
            bgColor="transparent"
            fgColor="#52525b"
            level="M"
          />
          <div className="lb-qr-label">Guest<br/>Check-in</div>
        </div>
      </div>
    </div>
  );
}
